import type { GuaranteeProgressView } from "../guarantee-types";
import type { MediaAssetView, MediaListView, MediaUsageRefView } from "../media-types";
import type {
  CommerceCollision,
  CommerceConnectionHealth,
  CommerceConnectionSummary,
  CommerceTestResult,
  CsvImportResult,
  CsvPreviewResult,
} from "../types";
import type {
  LoyaltyBalanceView,
  LoyaltyOverviewView,
  LoyaltyDrawResultView,
  LoyaltyRedeemResultView,
  ReferralLinkView,
} from "../loyalty-types";
import type {
  PackInstallView,
  PackReviewGroup,
  PackUninstallResult,
  VerticalPackCatalog,
} from "../vertical-pack-types";
import type {
  CatalogItemView,
  CatalogCurrencyView,
  CatalogSyncView,
  InstagramPostLinkView,
  ProductStatView,
  CatalogView,
  MessageTemplateView,
  MetaCatalogChoice,
  ProductSetDetailView,
  ProductSetView,
} from "../catalog-types";
import type {
  CatalogOrderSettingsView,
  ShippingAddressView,
  WaOrderDetailView,
  WaOrderView,
} from "../wa-order-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import { ApiError } from "../errors";
import type { WooCommercePreview, CatalogNumberMapping } from "../client-types";

export type CatalogApiMethods = Pick<
  ApiClient,
  | "installVerticalPack"
  | "getCatalogAvailability"
  | "listCatalogs"
  | "listMetaCatalogChoices"
  | "linkCatalog"
  | "autoConnectCatalog"
  | "listCatalogConnectChoices"
  | "connectExistingCatalog"
  | "updateCatalogSettings"
  | "setCatalogSourceMode"
  | "listCatalogProducts"
  | "addCatalogProduct"
  | "uploadProductImage"
  | "listMedia"
  | "uploadMedia"
  | "updateMedia"
  | "replaceMediaContent"
  | "deleteMedia"
  | "restoreMedia"
  | "getMediaUsage"
  | "updateCatalogProduct"
  | "renameCatalogCategory"
  | "deleteCatalogProduct"
  | "getCatalogCurrency"
  | "syncCatalog"
  | "publishCatalog"
  | "listInstagramPostLinks"
  | "setInstagramPostLink"
  | "getProductStats"
  | "getCatalogSync"
  | "listCatalogSyncs"
  | "resyncCatalogProduct"
  | "listProductSets"
  | "getProductSet"
  | "createProductSet"
  | "updateProductSet"
  | "deleteProductSet"
  | "listWaOrders"
  | "waOrderPdfUrl"
  | "waOrderDocumentLink"
  | "getWaOrder"
  | "createWaOrder"
  | "updateWaOrderStatus"
  | "setWaOrderShippingAddress"
  | "sendWaOrderPaymentReminder"
  | "getCatalogOrderSettings"
  | "updateCatalogOrderSettings"
  | "testCatalogPaymentLink"
  | "getCatalogMessageTemplates"
  | "updateCatalogMessageTemplates"
  | "getLoyaltyBalance"
  | "getLoyaltyOverview"
  | "redeemLoyaltyReward"
  | "adjustLoyaltyPoints"
  | "createReferralLink"
  | "drawContest"
  | "getGuaranteeTracking"
  | "optInGuarantee"
  | "chooseGuaranteeRefund"
  | "listVerticalPacks"
  | "reviewVerticalPack"
  | "uninstallVerticalPack"
  | "listCommerceConnections"
  | "getCommerceHealth"
  | "resyncCommerceConnection"
  | "patchCommerceConnection"
  | "rerunCommerceDeadLetter"
  | "listCommerceCollisions"
  | "resolveCommerceCollision"
  | "retryCommerceTest"
  | "testWooCommerce"
  | "connectWooCommerce"
  | "listCatalogMappings"
  | "setCatalogMapping"
  | "deleteCatalogMapping"
  | "previewCommerceCsv"
  | "importCommerceCsv"
>;

export function createCatalogApi({ request, ws, base }: HttpTransport): CatalogApiMethods {
  return {
    installVerticalPack: (id, packKey) =>
      request<PackInstallView>(
        "POST",
        `${ws(id)}/vertical-packs/${encodeURIComponent(packKey)}/install`,
        {},
      ),
    async getCatalogAvailability(id) {
      try {
        return (await request<{ available: boolean }>("GET", ws(id) + "/catalogs/availability"))
          .available;
      } catch (error) {
        // FB3-48: a nav probe never errors loudly; a denied plan/permission just hides the entry.
        if (error instanceof ApiError && error.status === 403) return false;
        throw error;
      }
    },
    async listCatalogs(id) {
      return (await request<{ catalogs: CatalogView[] }>("GET", ws(id) + "/catalogs")).catalogs;
    },
    async listMetaCatalogChoices(id, channelId) {
      return (
        await request<{ catalogs: MetaCatalogChoice[] }>(
          "GET",
          ws(id) + "/catalogs/available?channelId=" + encodeURIComponent(channelId),
        )
      ).catalogs;
    },
    async autoConnectCatalog(id, input) {
      return request<{ catalog: CatalogView; created: boolean; synced: number }>(
        "POST",
        ws(id) + "/catalogs/auto-connect",
        input,
      );
    },
    async listCatalogConnectChoices(id, channelId) {
      return (
        await request<{ catalogs: MetaCatalogChoice[] }>(
          "GET",
          ws(id) + "/catalogs/connect-choices?channelId=" + encodeURIComponent(channelId),
        )
      ).catalogs;
    },
    async connectExistingCatalog(id, input) {
      return request<{ catalog: CatalogView; created: boolean; synced: number }>(
        "POST",
        ws(id) + "/catalogs/connect-existing",
        input,
      );
    },
    async linkCatalog(id, input) {
      return (await request<{ catalog: CatalogView }>("POST", ws(id) + "/catalogs", input)).catalog;
    },
    async updateCatalogSettings(id, catalogId, input) {
      return (
        await request<{ catalog: CatalogView }>(
          "PATCH",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/settings",
          input,
        )
      ).catalog;
    },
    async setCatalogSourceMode(id, catalogId, input) {
      return (
        await request<{ catalog: CatalogView }>(
          "PUT",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/source-mode",
          input,
        )
      ).catalog;
    },
    async listCatalogProducts(id, catalogId) {
      return (
        await request<{ products: CatalogItemView[] }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/products",
        )
      ).products;
    },
    async uploadProductImage(id, file) {
      return request<{ url: string }>("POST", ws(id) + "/catalog/product-images", undefined, {
        body: file,
        contentType: file.type || "application/octet-stream",
      });
    },
    async listMedia(id, query) {
      const qs = new URLSearchParams();
      if (query?.kind) qs.set("kind", query.kind);
      if (query?.q) qs.set("q", query.q);
      if (query?.folder) qs.set("folder", query.folder);
      if (query?.sort) qs.set("sort", query.sort);
      if (query?.includeDeleted) qs.set("includeDeleted", "true");
      const s = qs.toString();
      return request<MediaListView>("GET", ws(id) + "/media" + (s ? "?" + s : ""));
    },
    async uploadMedia(id, file, options) {
      const qs = new URLSearchParams();
      if (options?.name) qs.set("name", options.name);
      if (options?.folder) qs.set("folder", options.folder);
      const s = qs.toString();
      return request<MediaAssetView>("POST", ws(id) + "/media" + (s ? "?" + s : ""), undefined, {
        body: file,
        contentType: file.type || "application/octet-stream",
      });
    },
    async updateMedia(id, mediaId, patch) {
      return request<MediaAssetView>(
        "PATCH",
        ws(id) + "/media/" + encodeURIComponent(mediaId),
        patch,
      );
    },
    async replaceMediaContent(id, mediaId, file) {
      return request<MediaAssetView>(
        "PUT",
        ws(id) + "/media/" + encodeURIComponent(mediaId) + "/content",
        undefined,
        { body: file, contentType: file.type || "application/octet-stream" },
      );
    },
    async deleteMedia(id, mediaId) {
      return request<MediaAssetView>("DELETE", ws(id) + "/media/" + encodeURIComponent(mediaId));
    },
    async restoreMedia(id, mediaId) {
      return request<MediaAssetView>(
        "POST",
        ws(id) + "/media/" + encodeURIComponent(mediaId) + "/restore",
      );
    },
    async getMediaUsage(id, mediaId) {
      return (
        await request<{ items: MediaUsageRefView[] }>(
          "GET",
          ws(id) + "/media/" + encodeURIComponent(mediaId) + "/usage",
        )
      ).items;
    },
    async addCatalogProduct(id, catalogId, input) {
      return (
        await request<{ product: CatalogItemView }>(
          "POST",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/products",
          input,
        )
      ).product;
    },
    async updateCatalogProduct(id, catalogId, productId, input) {
      return (
        await request<{ product: CatalogItemView }>(
          "PATCH",
          ws(id) +
            "/catalogs/" +
            encodeURIComponent(catalogId) +
            "/products/" +
            encodeURIComponent(productId),
          input,
        )
      ).product;
    },
    async renameCatalogCategory(id, catalogId, from, to) {
      return (
        await request<{ updated: number }>(
          "POST",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/categories/rename",
          { from, to },
        )
      ).updated;
    },
    async deleteCatalogProduct(id, catalogId, productId) {
      await request<void>(
        "DELETE",
        ws(id) +
          "/catalogs/" +
          encodeURIComponent(catalogId) +
          "/products/" +
          encodeURIComponent(productId),
      );
    },
    getCatalogCurrency: async (id, catalogId) =>
      (
        await request<{ currency: CatalogCurrencyView }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/currency",
        )
      ).currency,
    syncCatalog: async (id, catalogId) =>
      (
        await request<{ sync: CatalogSyncView }>(
          "POST",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/sync",
        )
      ).sync,
    listInstagramPostLinks: async (id, channelId) =>
      (
        await request<{ links: InstagramPostLinkView[] }>(
          "GET",
          ws(id) + "/catalog/instagram-links?channelId=" + encodeURIComponent(channelId),
        )
      ).links,
    setInstagramPostLink: async (id, link) => {
      await request<void>("PUT", ws(id) + "/catalog/instagram-links", link);
    },
    getProductStats: (id) =>
      request<{ days: number; stats: ProductStatView[] }>(
        "GET",
        ws(id) + "/catalog/product-stats?range=30d",
      ),
    publishCatalog: async (id, catalogId) =>
      (
        await request<{ sync: CatalogSyncView | null }>(
          "POST",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/publish-all",
        )
      ).sync,
    getCatalogSync: async (id, catalogId, syncId) =>
      (
        await request<{ sync: CatalogSyncView }>(
          "GET",
          ws(id) +
            "/catalogs/" +
            encodeURIComponent(catalogId) +
            "/sync/" +
            encodeURIComponent(syncId),
        )
      ).sync,
    listCatalogSyncs: async (id, catalogId) =>
      (
        await request<{ syncs: CatalogSyncView[] }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/sync",
        )
      ).syncs,
    resyncCatalogProduct: async (id, catalogId, productId) =>
      (
        await request<{ sync: CatalogSyncView }>(
          "POST",
          ws(id) +
            "/catalogs/" +
            encodeURIComponent(catalogId) +
            "/products/" +
            encodeURIComponent(productId) +
            "/resync",
        )
      ).sync,
    listProductSets: async (id, catalogId) =>
      (
        await request<{ sets: ProductSetView[] }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/product-sets",
        )
      ).sets,
    getProductSet: (id, catalogId, setId) =>
      request<ProductSetDetailView>(
        "GET",
        ws(id) +
          "/catalogs/" +
          encodeURIComponent(catalogId) +
          "/product-sets/" +
          encodeURIComponent(setId),
      ),
    createProductSet: (id, catalogId, input) =>
      request<ProductSetDetailView>(
        "POST",
        ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/product-sets",
        input,
      ),
    updateProductSet: (id, catalogId, setId, input) =>
      request<ProductSetDetailView>(
        "PATCH",
        ws(id) +
          "/catalogs/" +
          encodeURIComponent(catalogId) +
          "/product-sets/" +
          encodeURIComponent(setId),
        input,
      ),
    deleteProductSet: (id, catalogId, setId) =>
      request<void>(
        "DELETE",
        ws(id) +
          "/catalogs/" +
          encodeURIComponent(catalogId) +
          "/product-sets/" +
          encodeURIComponent(setId),
      ),
    listWaOrders: async (id, query) =>
      (
        await request<{ orders: WaOrderView[] }>(
          "GET",
          ws(id) +
            "/wa-orders?limit=" +
            (query?.limit ?? 200) +
            (query?.status ? "&status=" + query.status : "") +
            (query?.contactId ? "&contactId=" + encodeURIComponent(query.contactId) : ""),
        )
      ).orders,
    waOrderPdfUrl: (id, orderId) => `${base}${ws(id)}/wa-orders/${encodeURIComponent(orderId)}/pdf`,
    waOrderDocumentLink: (id, orderId) =>
      request<{ url: string }>(
        "POST",
        ws(id) + "/wa-orders/" + encodeURIComponent(orderId) + "/document-link",
        {},
      ),
    getWaOrder: (id, orderId) =>
      request<WaOrderDetailView>("GET", ws(id) + "/wa-orders/" + encodeURIComponent(orderId)),
    createWaOrder: (id, input) => request<WaOrderDetailView>("POST", ws(id) + "/wa-orders", input),
    updateWaOrderStatus: async (id, orderId, status) =>
      (
        await request<{ order: WaOrderView }>(
          "PATCH",
          ws(id) + "/wa-orders/" + encodeURIComponent(orderId),
          { status },
        )
      ).order,
    setWaOrderShippingAddress: async (id, orderId, address) =>
      (
        await request<{ shippingAddress: ShippingAddressView }>(
          "PUT",
          ws(id) + "/wa-orders/" + encodeURIComponent(orderId) + "/shipping-address",
          address,
        )
      ).shippingAddress,
    sendWaOrderPaymentReminder: async (id, orderId) => {
      await request(
        "POST",
        ws(id) + "/wa-orders/" + encodeURIComponent(orderId) + "/payment-reminder",
      );
    },
    getCatalogOrderSettings: async (id, catalogId) =>
      (
        await request<{ settings: CatalogOrderSettingsView }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/order-settings",
        )
      ).settings,
    updateCatalogOrderSettings: async (id, catalogId, settings) =>
      (
        await request<{ settings: CatalogOrderSettingsView }>(
          "PUT",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/order-settings",
          { settings },
        )
      ).settings,
    testCatalogPaymentLink: async (id, catalogId) =>
      (
        await request<{ link: { url: string; testMode: boolean } }>(
          "POST",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/payment-link-test",
          {},
        )
      ).link,
    getCatalogMessageTemplates: async (id, catalogId) =>
      (
        await request<{ templates: MessageTemplateView[] }>(
          "GET",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/message-templates",
        )
      ).templates,
    updateCatalogMessageTemplates: async (id, catalogId, templates) =>
      (
        await request<{ templates: MessageTemplateView[] }>(
          "PUT",
          ws(id) + "/catalogs/" + encodeURIComponent(catalogId) + "/message-templates",
          { templates },
        )
      ).templates,
    getLoyaltyOverview: (id) => request<LoyaltyOverviewView>("GET", ws(id) + "/loyalty/overview"),
    getLoyaltyBalance: (id, contactId) =>
      request<LoyaltyBalanceView>(
        "GET",
        ws(id) + "/loyalty/wallet/" + encodeURIComponent(contactId),
      ),
    redeemLoyaltyReward: (id, input) =>
      request<LoyaltyRedeemResultView>("POST", ws(id) + "/loyalty/redeem", input),
    adjustLoyaltyPoints: (id, input) =>
      request<LoyaltyBalanceView>("POST", ws(id) + "/loyalty/adjust", input),
    createReferralLink: (id, contactId) =>
      request<ReferralLinkView>("POST", ws(id) + "/referrals/generate-link", { contactId }),
    drawContest: (id, { contestId, confirmEntrantCount }) =>
      request<LoyaltyDrawResultView>(
        "POST",
        ws(id) + "/contests/" + encodeURIComponent(contestId) + "/draw",
        { confirmEntrantCount },
      ),
    async getGuaranteeTracking(id) {
      return (
        await request<{ tracking: GuaranteeProgressView | null }>(
          "GET",
          ws(id) + "/guarantee/tracking",
        )
      ).tracking;
    },
    optInGuarantee: (id) => request<void>("POST", ws(id) + "/guarantee/opt-in"),
    chooseGuaranteeRefund: (id, method) =>
      request<void>("POST", ws(id) + "/guarantee/refund", { method }),
    listVerticalPacks: (id) => request<VerticalPackCatalog>("GET", ws(id) + "/vertical-packs"),
    async reviewVerticalPack(id, key) {
      return (
        await request<{ groups: PackReviewGroup[] }>(
          "GET",
          ws(id) + "/vertical-packs/" + encodeURIComponent(key) + "/review",
        )
      ).groups;
    },
    uninstallVerticalPack: (id, installId) =>
      request<PackUninstallResult>(
        "POST",
        ws(id) + "/pack-installs/" + encodeURIComponent(installId) + "/uninstall",
        {},
      ),
    async listCommerceConnections(id) {
      return (
        await request<{ connections: CommerceConnectionSummary[] }>(
          "GET",
          `${ws(id)}/commerce/connections`,
        )
      ).connections;
    },
    getCommerceHealth: (id, connectionId) =>
      request<CommerceConnectionHealth>(
        "GET",
        `${ws(id)}/commerce/connections/${encodeURIComponent(connectionId)}/health`,
      ),
    resyncCommerceConnection: (id, connectionId, idempotencyKey) =>
      request<{ queued: boolean; deduplicated: boolean }>(
        "POST",
        `${ws(id)}/commerce/connections/${encodeURIComponent(connectionId)}/resync`,
        undefined,
        undefined,
        { "idempotency-key": idempotencyKey },
      ),
    patchCommerceConnection: (id, connectionId, patch) =>
      request<CommerceConnectionHealth>(
        "PATCH",
        `${ws(id)}/commerce/connections/${encodeURIComponent(connectionId)}`,
        patch,
      ),
    async rerunCommerceDeadLetter(id, connectionId, entryId) {
      await request(
        "POST",
        `${ws(id)}/commerce/connections/${encodeURIComponent(connectionId)}/dead-letters/${encodeURIComponent(entryId)}/rerun`,
      );
    },
    async listCommerceCollisions(id) {
      return (
        await request<{ collisions: CommerceCollision[] }>("GET", `${ws(id)}/commerce/collisions`)
      ).collisions;
    },
    async resolveCommerceCollision(id, sku, keepProductId) {
      await request("POST", `${ws(id)}/commerce/collisions/resolve`, { sku, keepProductId });
    },
    retryCommerceTest: (id, connectionId) =>
      request<CommerceTestResult>(
        "POST",
        `${ws(id)}/commerce/connect/connections/${encodeURIComponent(connectionId)}/test`,
      ),
    testWooCommerce: (id, body) =>
      request<WooCommercePreview>("POST", `${ws(id)}/commerce/connect/woocommerce/test`, body),
    connectWooCommerce: (id, body) =>
      request<{ id: string }>("POST", `${ws(id)}/commerce/connect/woocommerce`, body),
    listCatalogMappings: (id) =>
      request<{ items: CatalogNumberMapping[] }>("GET", `${ws(id)}/catalog-mappings`),
    setCatalogMapping: (id, phoneNumberId, metaCatalogId) =>
      request<CatalogNumberMapping>(
        "PUT",
        `${ws(id)}/catalog-mappings/${encodeURIComponent(phoneNumberId)}`,
        { metaCatalogId },
      ),
    deleteCatalogMapping: async (id, phoneNumberId) => {
      await request("DELETE", `${ws(id)}/catalog-mappings/${encodeURIComponent(phoneNumberId)}`);
    },
    previewCommerceCsv: (id, body) =>
      request<CsvPreviewResult>("POST", `${ws(id)}/commerce/connect/custom-csv/preview`, body),
    importCommerceCsv: (id, body) =>
      request<CsvImportResult>("POST", `${ws(id)}/commerce/connect/custom-csv/import`, body),
  };
}
